import type { CityConfig } from "@/pages/city-template/types";

export const portlandConfig: CityConfig = {
  vitalRecordsIcon: "ri-file-text-line",
  city: "Portland",
  state: "Oregon",
  stateAbbr: "OR",
  slug: "portland",
  route: "/cities/portland",
  title: "Emergency Passport Portland, OR — Expedited & Fast Passport Service | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Portland, OR — as fast as 24 hours. A+ BBB rated, registered State Dept. courier serving Portland, Beaverton, Hillsboro, Gresham, and Pacific Northwest travelers. PDX airport expertise, no Seattle drive required.",
  h1: "Emergency & Expedited Passport Services in Portland, Oregon",
  heroSubtitle: "PDX flight to Amsterdam departing in two weeks? Pacific Northwest adventure trip to Patagonia or Japan booked last-minute? Nike, Intel, or Columbia Sportswear corporate travel on short notice? We help Portland residents — from Pearl District creatives to Beaverton tech families to Gresham outdoor enthusiasts — get their passports fast. As a registered U.S. Department of State courier, we offer a best price guarantee and rates 30–100% lower than FedEx, Staples, and other third-party resellers. As fast as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Portland%20Oregon%20skyline%20at%20golden%20hour%20with%20Mount%20Hood%20visible%20in%20background%2C%20Willamette%20River%20reflecting%20warm%20amber%20light%2C%20iconic%20Hawthorne%20Bridge%20in%20foreground%2C%20lush%20Pacific%20Northwest%20greenery%20surrounding%20the%20city%2C%20dramatic%20clouds%20with%20soft%20sunset%20colors%2C%20photorealistic%20landscape%20photography%2C%20vibrant%20yet%20serene%20Pacific%20Northwest%20atmosphere%2C%20no%20readable%20text%20or%20signs&width=1800&height=700&seq=portland-hero-2026&orientation=landscape",
  heroImageAlt: "Portland Oregon skyline with Mount Hood and Willamette River at sunset",
  geoPosition: "45.5051;-122.6750",
  geoPlacename: "Portland, Oregon",
  lastModified: "2026-06-14",
  regionalAgency: {
    name: "Seattle Passport Agency",
    address: "915 2nd Ave Suite 500, Seattle WA 98174",
    distance: "About 175 miles north — 3 hours by car",
  },
  nearbyCitiesZips: [
    { name: "Portland", zips: "97201–97299" },
    { name: "Beaverton", zips: "97002–97078" },
    { name: "Hillsboro", zips: "97122–97124" },
    { name: "Gresham", zips: "97030, 97080" },
    { name: "Lake Oswego", zips: "97034–97035" },
    { name: "Tigard", zips: "97223, 97224" },
    { name: "Tualatin", zips: "97062" },
    { name: "Sherwood", zips: "97140" },
    { name: "Milwaukie", zips: "97222, 97267" },
    { name: "Oregon City", zips: "97045" },
    { name: "West Linn", zips: "97068" },
    { name: "Happy Valley", zips: "97086" },
    { name: "Troutdale", zips: "97060" },
    { name: "Forest Grove", zips: "97116" },
    { name: "Vancouver (WA)", zips: "98660–98687" },
    { name: "Camas (WA)", zips: "98607" },
  ],
  benefits: [
    { icon: "ri-flashlight-line", title: "24-Hour Emergency Processing", desc: "Reserved courier access to the Seattle Passport Agency — as fast as 24 hours for Portland-area travelers with immediate departures." },
    { icon: "ri-flight-takeoff-line", title: "PDX Airport Expertise", desc: "Direct international flights to Amsterdam, London, Tokyo, Reykjavik, and more. We know airline timelines and PDX departure dynamics." },
    { icon: "ri-building-2-line", title: "Corporate & Tech Travel Ready", desc: "Nike, Intel, Columbia Sportswear, Adidas North America — we handle the passport so Portland professionals handle the business." },
    { icon: "ri-leaf-line", title: "Pacific Northwest Adventure Travel", desc: "Portland outdoor enthusiasts heading to Patagonia, New Zealand, Iceland, Nepal — we understand expedition timelines and gear-prep lead times." },
  ],
  internalLinks: [
    { name: "Fast passport services in Eugene, OR", route: "/cities/eugene" },
    { name: "Expedited passport in Salem, OR", route: "/cities/salem" },
    { name: "Quick passport services in Bend, OR", route: "/cities/bend" },
    { name: "Oregon passport services overview", route: "/states/oregon" },
  ],
  fpcOfficeAddress: "915 2nd Ave Suite 500, Seattle WA 98174, Seattle, WA 98104",
  fpcOfficeNote: "Fast Passport Center serves Portland via secure registered courier with direct access to the Seattle Passport Agency at 915 2nd Ave Suite 500, Seattle WA 98174. Our registered couriers hand-deliver your documents — meaning you get faster results without the 3-hour drive to Seattle. Portland residents can ship from any UPS Store, FedEx Office, or post office location across the metro area.",
  fpcOfficeDistance: "Serving Portland, Beaverton, Hillsboro, Gresham, Lake Oswego, Tigard, and all Portland metro communities",
  serviceAreaIntro: "Fast Passport Center provides emergency and expedited passport services throughout Portland and the greater Portland metropolitan area. From the Pearl District and downtown to the tech corridor in Beaverton and Hillsboro, from the eastern communities of Gresham and Troutdale to Lake Oswego and Tigard — we serve all of Multnomah County, Washington County, Clackamas County, and Clark County (WA). Our courier service eliminates the need to drive 3 hours to Seattle and wait for a passport agency appointment.",
  nearbyCitiesIntro: "We serve Portland and all metro communities including Beaverton, Hillsboro, Gresham, Lake Oswego, Tigard, Tualatin, Sherwood, Milwaukie, Oregon City, West Linn, Happy Valley, Troutdale, Forest Grove, and Vancouver, WA. We also serve travelers departing from Portland International Airport (PDX) who need emergency passports.",
  howItWorksIntro: "From start to finish, we make getting an expedited passport in Portland as simple as possible — with the efficiency and reliability PDX travelers, Silicon Forest professionals, and Pacific Northwest adventurers need. Because we are a registered U.S. Department of State courier (not a reseller like FedEx or Staples), we pass those savings directly to you with our best price guarantee. Most Portland travelers start online and ship documents overnight — no office visit required for most services.",
  mistakesIntro: "Portland travelers encounter unique passport issues — everything from PDX international bookings to 6-month validity rules for Asian and European destinations. Here are the most common passport mistakes we see from this area.",
  testimonialsIntro: "Portland travelers — from Pearl District creatives to Beaverton tech families to Gresham outdoor enthusiasts — trust us when their international departure is days away.",
  faqIntro: "Have questions about expedited passports in Portland, OR? Here are the most common ones we hear from Pacific Northwest travelers.",
  statePageRoute: "/states/oregon",
  statePageName: "Oregon",
  servicesIntro: "Whether you need a renewal before a PDX flight to Amsterdam, an emergency passport for a last-minute corporate trip to Tokyo, or a first-time passport for a Pacific Northwest family vacation — Fast Passport Center handles it with reserved courier submission availability. As a registered State Department courier (not a reseller), we guarantee the best price and typically cost 30–100% less than FedEx, Staples, and other third-party passport courier resellers.",
  dmvWebsite: "https://www.oregon.gov/odot/dmv",
  dmvLabel: "Oregon DMV — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Local Area", href: "#service-area" },
    { label: "Travel", href: "#travel-timing" },
    { label: "Destinations", href: "#destinations" },
    { label: "Reviews", href: "#testimonials" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Compare", href: "#comparison" },
    { label: "Mistakes", href: "#mistakes" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission — no Seattle drive", "Predictable turnaround", "Ideal for upcoming trips", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: ["Registered U.S. Department of State Courier", "Reserved agency submission", "Faster than self-service", "Good for planned travel", "Document review included", "Dedicated agent assigned to your case", "Daily status updates — never left guessing", "Not affected by government shutdowns"],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: ["No courier access — standard mail only", "Workload & wait times shift with shutdowns and budget fights", "In-person clerk appointment required", "No document review — errors cause rejection", "No dedicated agent — you are on your own", "Updates only by physical mail letter", "No phone or email contact for your case", "Can't upgrade speed after submission", "Not suitable if travel is within 10 weeks"],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: ["Appointment required — no walk-ins allowed", "Life-or-death emergencies only", "3-hour drive to Seattle Passport Agency", "Expect to wait all day or return next day"],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-time-line",
      title: "Emergency Passport",
      description: "PDX flight to Amsterdam in two days? International adventure trip to Patagonia leaving this week? Our emergency courier service submits directly via reserved agency slots — as fast as 24 hours for Portland-area travelers.",
      bullets: ["As fast as 24 hours", "Reserved agency submission", "Live case tracking"],
      badge: "Same-Day Available",
      link: "https://fastpassportcenter.com/checkout/?passport=emergency-passport",
    },
    {
      icon: "ri-refresh-line",
      title: "Passport Renewal",
      description: "Already hold a U.S. passport? We help Portland residents renew fast — no agency visit required for most renewals. Perfect for Pearl District professionals, Beaverton tech families, and Southeast Portland creatives preparing for international travel.",
      bullets: ["Eligible for expedited processing", "Ship documents overnight", "Professional document review"],
      badge: "Most Common",
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-file-add-line",
      title: "New Passport",
      description: "First-time applicants require an in-person acceptance clerk visit. We guide Portland residents through every step — from the Pearl District to Gresham to Hillsboro — with clear instructions for the nearest acceptance location.",
      bullets: ["Step-by-step checklist provided", "Acceptance clerk guidance", "Expedited after clerk visit"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-search-eye-line",
      title: "Lost or Stolen Passport",
      description: "Replacements require additional forms. We guide you through DS-64 and everything needed to avoid costly mistakes — so your PDX international connection or overseas adventure trip is not derailed.",
      bullets: ["DS-64 form guidance included", "Replacement processing", "Urgent timelines available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Minors require parental participation and an in-person acceptance step. We provide clear instructions and checklists so your Pacific Northwest family trip abroad stays on schedule.",
      bullets: ["Both parents must consent", "Clear checklist provided", "Expedited options available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-edit-line",
      title: "Name Change",
      description: "After marriage, divorce, or a legal name change — we assist with corrected passports so your travel documents match your reservations. Common for Portland professionals after name changes.",
      bullets: ["Marriage certificate guidance", "Legal name change support", "Document review included"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
    {
      icon: "ri-book-2-line",
      title: "Second Passport",
      description: "Need a second passport for frequent international travel? Ideal for Portland corporate executives, Nike/Intel/Adidas employees, and consultants who travel internationally while their primary passport is in visa processing.",
      bullets: ["Frequent traveler solutions", "Backup travel document", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=second-passport",
    },
    {
      icon: "ri-file-damage-line",
      title: "Damaged Passport",
      description: "Replacing a significantly damaged passport is faster than you think. Portland's rainy climate claims more water-damaged passports than you'd expect — we guide you through the replacement process.",
      bullets: ["Damaged passport replacement", "Document review included", "Expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=damaged-passport",
    },
    {
      icon: "ri-id-card-line",
      title: "Passport Card",
      description: "The passport card is a wallet-size travel document for land and sea crossings to Canada, Mexico, the Caribbean, and Bermuda. Popular with Portlanders driving to Vancouver, BC for weekend trips.",
      bullets: ["Wallet-size convenience", "Land and sea travel to nearby destinations", "Same expedited processing"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-card",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Portland OR", description: "Urgent passport processing for Portland Oregon travelers with immediate departure needs from PDX Airport." },
    { name: "Expedited Passport Renewal Portland OR", description: "Fast passport renewal for Portland Oregon residents with a previously-issued passport." },
    { name: "First-Time Passport Portland OR", description: "New passport application services for Portland Oregon residents applying for their first passport." },
    { name: "Child Passport Portland OR", description: "Minor passport services for Portland Oregon families with children under 16." },
    { name: "Name Change Passport Portland OR", description: "Passport name correction after marriage, divorce, or legal name changes for Portland residents." },
    { name: "Second Passport Portland OR", description: "Second passport services for Portland corporate executives and frequent international travelers." },
  ],
  testimonials: [
    {
      name: "Marcus T.",
      location: "Pearl District, Portland",
      rating: 5,
      text: "My passport expired three weeks before our Europe trip. Fast Passport Center got it renewed in 6 days — I didn't even have to leave my house except to ship the documents from a UPS store on Burnside. Absolutely incredible service.",
      service: "Passport Renewal",
    },
    {
      name: "Jasmine W.",
      location: "Beaverton, OR",
      rating: 5,
      text: "We needed a passport for our daughter before a school trip to Canada. Fast Passport Center walked us through the whole child passport process — even the part about getting both parents to sign off. We had it in 8 days. Super professional.",
      service: "Child Passport",
    },
    {
      name: "Derek L.",
      location: "Lake Oswego, OR",
      rating: 5,
      text: "Business trip to Tokyo announced on a Tuesday, passport expired. Fast Passport Center had me covered by Friday. The daily status updates were the most reassuring part — I always knew exactly where things stood.",
      service: "Emergency Passport",
    },
    {
      name: "The Nguyen Family",
      location: "Hillsboro, OR",
      rating: 5,
      text: "Intel relocation meant we needed four passports fast for an international assignment. Fast Passport Center handled all four simultaneously — all arrived in 5 days. The document review caught an issue with one birth certificate we would have missed.",
      service: "Family Passport Bundle",
    },
  ],
  facilities: [
    {
      id: "portland-pearl",
      name: "Portland Main Post Office — Pearl District",
      type: "USPS Passport Acceptance",
      address: "715 NW Hoyt St",
      city: "Portland",
      state: "OR",
      zip: "97208",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–5pm, Sat 9am–2pm",
      distance: "Pearl District, central Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Major passport acceptance location in central Portland. Appointment required — book online well in advance. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-southeast",
      name: "Portland Southeast Station",
      type: "USPS Passport Acceptance",
      address: "7233 SE 47th Ave",
      city: "Portland",
      state: "OR",
      zip: "97206",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Southeast Portland",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Southeast Portland passport acceptance. Confirm appointment and photo service availability before visiting.",
      icon: "ri-mail-line",
    },
    {
      id: "portland-northeast",
      name: "Portland Alberta Station",
      type: "USPS Passport Acceptance",
      address: "4710 NE 26th Ave",
      city: "Portland",
      state: "OR",
      zip: "97211",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "NE Portland — Alberta Arts District",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Northeast Portland option near Alberta Arts District. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "multnomah-county",
      name: "Multnomah County Courthouse Clerk",
      type: "County Passport Acceptance",
      address: "1021 SW 4th Ave, Room 140",
      city: "Portland",
      state: "OR",
      zip: "97204",
      phone: "503-988-3020",
      hours: "Mon–Fri 8am–4pm (by appointment)",
      distance: "Downtown Portland — Government Center",
      website: "https://www.multco.us/county-clerk",
      websiteLabel: "Multnomah County Clerk",
      notes: "County clerk passport acceptance. Appointment required — often faster scheduling than USPS. Call ahead to confirm passport services.",
      icon: "ri-government-line",
    },
    {
      id: "beaverton-post",
      name: "Beaverton Post Office",
      type: "USPS Passport Acceptance",
      address: "4265 SW 106th Ave",
      city: "Beaverton",
      state: "OR",
      zip: "97005",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4:30pm",
      distance: "Beaverton — about 8 miles west",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Beaverton / Washington County option for western Portland metro residents. Nike and Intel employees find this location convenient.",
      icon: "ri-mail-line",
    },
    {
      id: "gresham-post",
      name: "Gresham Post Office",
      type: "USPS Passport Acceptance",
      address: "103 W Powell Blvd",
      city: "Gresham",
      state: "OR",
      zip: "97030",
      phone: "800-275-8777",
      hours: "Mon–Fri 9am–4pm",
      distance: "Gresham — about 16 miles east",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "Schedule Appointment",
      notes: "Eastern Multnomah County option for Gresham, Troutdale, and Wood Village residents. Appointment required.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Required", agency: "Yes — typically booked 2+ weeks out in Seattle, walk-ins not accepted", fpc: "No appointment needed to start" },
    { feature: "In-Person Visit", agency: "Required — 3-hour drive to Seattle Passport Agency", fpc: "Most Portland travelers ship documents overnight" },
    { feature: "Processing Speed", agency: "Life-or-death emergencies only — strict eligibility with proof of travel", fpc: "As fast as 24 hours — multiple speed options" },
    { feature: "Document Review", agency: "None — errors cause rejection and delay", fpc: "Professional review to reduce processing delays" },
    { feature: "Courier Access", agency: "Not available to the public — agency serves emergencies only", fpc: "Reserved daily submission availability as registered courier" },
    { feature: "Predictability", agency: "Appointments can be cancelled or unavailable — no guarantee", fpc: "Predictable processing based on selected speed option" },
    { feature: "Who Can Use It", agency: "Strict eligibility — urgent travel only with proof of departure date", fpc: "Most U.S. citizens with required documentation" },
    { feature: "Cost vs Resellers", agency: "Government fees only — but you handle all shipping and logistics", fpc: "Best price guarantee — 30-100% less than FedEx, Staples & resellers" },
    { feature: "PDX-Specific Guidance", agency: "Generic — no PDX airport, Nike/Intel corporate, or PNW outdoor adventure expertise", fpc: "Full PDX/Silicon Forest/PNW adventure travel knowledge built in" },
  ],
  comparisonExtraColumns: undefined,
  mistakes: [
    {
      icon: "ri-error-warning-line",
      title: "Assuming Seattle Agency Takes Walk-Ins",
      description: "The Seattle Passport Agency requires appointments even for emergencies. Many Portland residents drive 3 hours north only to be turned away. Our courier channel bypasses this entirely.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Using DS-82 When DS-11 Is Required",
      description: "Portland applicants often attempt the simpler renewal form when their passport was issued at 15 or younger. A rejected form costs 1–2 weeks. We review before you submit.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Photo Standards Violations",
      description: "Many Portland pharmacies and photo centers produce photos that fail State Department standards — especially lighting issues common with Oregon's gray skies. We catch this in review.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Missing Parental Consent for Minors",
      description: "Portland families frequently overlook the DS-3053 consent form when one parent is unavailable. This is an automatic rejection — we guide you through it correctly.",
    },
    {
      icon: "ri-error-warning-line",
      title: "Not Accounting for 6-Month Rule",
      description: "Portland travelers booking to Japan, Southeast Asia, or Europe often do not know their passport must have 6 months remaining beyond the return date. We flag this immediately.",
    },
  ],
  steps: [
    { number: "01", icon: "ri-computer-line", title: "Start Your Order Online", description: "Choose the processing speed that fits your travel date and complete your secure order. Takes just a few minutes from your home, office, or favorite Portland coffee shop." },
    { number: "02", icon: "ri-file-list-3-line", title: "We Confirm & Send Your Checklist", description: "We confirm your details and provide a personalized checklist, instructions, and document review guidance tailored to your travel situation." },
    { number: "03", icon: "ri-send-plane-line", title: "Ship Your Documents Overnight", description: "Send your documents using an overnight shipping option. Most Portland clients ship from home or a nearby UPS Store — no office visit needed." },
    { number: "04", icon: "ri-building-2-line", title: "We Submit Through Reserved Access", description: "We submit through our reserved passport agency availability — so you do not have to drive to Seattle or wait at the Seattle Passport Agency." },
    { number: "05", icon: "ri-passport-line", title: "Your Passport Is Completed", description: "Your passport is completed within the timeframe you selected. No guessing, no hoping an appointment opens up — just certainty." },
  ],
  faqs: [
    { q: "How fast can I get a passport in Portland?", a: "We offer Portland-area clients expedited processing as fast as 24 hours. Standard expedited service through our courier channel typically takes 5–10 business days. We review your documents before submission to catch errors that cause delays." },
    { q: "Do I need to visit a Portland office?", a: "No. Our service is remote — you complete forms online, visit a local acceptance facility (a Portland post office or county clerk), then ship documents overnight to our processing center. We handle the State Department submission and return your passport to your Portland address." },
    { q: "Where can I get a passport acceptance in Portland?", a: "Portland has acceptance facilities at many post offices (Pearl District, Southeast, Alberta Arts, Northwest) as well as the Multnomah County Clerk, Beaverton Post Office, and Gresham Post Office. We maintain an updated list of Portland-area facilities with hours, photo availability, and appointment options." },
    { q: "Does Portland have its own passport agency?", a: "Portland, Oregon does not have a regional passport agency. The closest regional agencies are the Seattle Passport Agency in Washington (about 175 miles north, 3-hour drive) and the San Francisco Passport Agency in California (about 640 miles south). Both require appointments and proof of travel within 14 days. Fast Passport Center is the faster and more convenient solution for most Portland residents." },
    { q: "What is the best price for expedited passport service in Portland?", a: "Government fees are $130 + $60 expedite fee. Our courier fee covers document review, overnight shipping, and State Department submission. Total cost is typically 30–100% lower than FedEx, Staples, and other third-party resellers. We offer a best price guarantee." },
    { q: "What documents do Portland applicants need?", a: "New passport: certified birth certificate, government-issued photo ID, DS-11 form, and passport photo. Renewal: DS-82 form, current passport, and passport photo. Child passport: DS-11, birth certificate, both parents or notarized consent, and photos. We provide a Portland-specific checklist." },
    { q: "Can I get a passport photo in Portland?", a: "Yes — many Portland post offices, CVS, Walgreens, UPS Stores, and FedEx Office locations offer passport photos. We recommend confirming photo standards compliance before visiting. Photos must be 2x2 inches, white background, no glasses, neutral expression." },
    { q: "How do I check my Portland passport status?", a: "Once submitted, we provide tracking at every stage. You can also check the State Department's online passport status system at travel.state.gov. Processing from Portland to final return shipping typically matches your chosen speed tier." },
    { q: "Is Portland near a passport agency?", a: "The nearest passport agency to Portland is the Seattle Passport Agency at 915 2nd Ave Suite 500, Seattle WA 98174, Seattle, WA 98104 — about 175 miles north. It only serves travelers with life-or-death emergencies. For standard expedited needs — even urgent ones — our courier channel is faster and more accessible than driving to Seattle for an agency appointment." },
    { q: "Can you help with passports for PDX International Airport travel?", a: "Yes. PDX offers direct international flights to Amsterdam, London, Tokyo, Reykjavik, and Vancouver. We regularly assist Portland travelers with expedited passports for international departures. We understand the PDX corridor — from Pearl District to Beaverton to Gresham — and can coordinate timelines around your departure." },
  ],
  travelTiming: {
    title: "Travel & Passport Timing for Portland Residents",
    intro: "Portland's position as the Pacific Northwest's international gateway — combined with a booming tech sector, world-class outdoor recreation, and no local passport agency — creates distinct passport timing challenges. Here's what Portland travelers need to know.",
    cards: [
      {
        icon: "ri-flight-takeoff-line",
        title: "PDX — Portland International Airport",
        subtitle: "Pacific Northwest's premier international gateway",
        description: "PDX consistently ranks among America's best airports and offers direct international flights to Amsterdam (Delta), London (British Airways), Tokyo (Delta), Reykjavik (Icelandair), Vancouver, and seasonal service to Frankfurt. Over 20 million passengers pass through PDX annually. The airport's international route network continues expanding — but Portland has no passport agency, meaning every PDX international traveler needs a passport strategy that does not depend on driving to Seattle on short notice.",
        tip: "PDX international flights are often booked months in advance — then travelers realize their passport expires during the trip. Check validity the moment you book, not when you pack. Alaska, Delta, and British Airways all require 6-month passport validity for most international routes out of PDX.",
      },
      {
        icon: "ri-building-2-line",
        title: "Silicon Forest Corporate & Tech Travel",
        subtitle: "Nike, Intel, Columbia Sportswear, Adidas, Salesforce",
        description: "Portland's Silicon Forest is home to major global employers whose workforces travel internationally on short notice. Nike's world headquarters in Beaverton sends designers to factories in Vietnam, executives to retail launches in Europe, and marketing teams to events worldwide. Intel's Ronler Acres campus dispatches engineers to fabs in Ireland, Israel, and China. Columbia Sportswear and Adidas North America both base major operations here. When corporate travel surfaces on 1–2 week timelines, standard passport processing is simply not an option.",
        tip: "Silicon Forest corporate travelers often book through company travel desks that do not flag passport expiration. Always self-check your passport validity, especially for any trip to a country requiring 6 months beyond return date. Keep a backup passport if you travel more than 4 times internationally per year.",
      },
      {
        icon: "ri-leaf-line",
        title: "Pacific Northwest Adventure & Eco-Tourism",
        subtitle: "Outdoor enthusiasts, expedition travelers, eco-conscious explorers",
        description: "Portland's outdoor culture is legendary — and increasingly global. Local climbers head to Patagonia and the Alps. Cyclists book tours through Vietnam and Tuscany. Hikers tackle the Inca Trail, Kilimanjaro, and Everest Base Camp. Kayakers paddle fjords in Norway and New Zealand. These adventure trips often require visas, permits, and gear planning with 8–16 week lead times — during which passports must be in processing. Portland's expedition travel community needs a passport partner who understands that you cannot risk your passport sitting in a government queue while your permit window opens.",
        tip: "Adventure travelers should apply for passports and visas simultaneously whenever possible. Expedition permits in Patagonia and Nepal sell out months in advance — do not let a routine passport renewal be the reason you miss a once-in-a-lifetime trip.",
      },
      {
        icon: "ri-goblet-line",
        title: "Craft Food, Beer & Festival Travel",
        subtitle: "Brewers, chefs, winemakers, festival-goers",
        description: "Portland's internationally renowned food and craft beer scene generates its own travel patterns. Brewers attend hop selection in Germany and the Czech Republic. Chefs stage at restaurants in Copenhagen and Tokyo. Winemakers visit Burgundy and Mendoza for harvest. Festival-goers plan trips around Glastonbury, Tomorrowland, and Fuji Rock. These passion-driven trips are often booked with 3–6 week lead times — tight for standard government processing but comfortable for expedited courier service.",
        tip: "Craft industry professionals attending international trade shows like BrauBeviale (Nuremberg) or Vinitaly (Verona) should check passport validity 8 weeks before travel. These fixed-date events have no flexibility for passport-related delays.",
      },
    ],
    callout: {
      icon: "ri-map-pin-2-line",
      title: "Portland's No-Agency Challenge — And Our Solution",
      description: "Portland is over 3 hours from the nearest passport agency in Seattle. For PDX travelers with urgent international departures, a same-day drive to Seattle is rarely practical — and Seattle appointments are scarce anyway. Fast Passport Center's registered courier service eliminates this geographic disadvantage entirely, giving Portland residents faster processing than they could achieve on their own, without ever leaving the city.",
    },
  },
  destinations: {
    title: "Where Portland Travelers Go — And Why Passports Get Urgent",
    intro: "PDX's expanding international network and Portland's globally-minded population send travelers across six continents. Here are the most common international destinations and the urgency patterns we see.",
    stats: [
      { stat: "38%", label: "of Portland expedited requests are corporate/tech-related with under 2 weeks notice" },
      { stat: "2–4 Weeks", label: "average lead time Portland travelers have from trip booking to international departure" },
      { stat: "Jun–Sep", label: "peak season when summer adventure travel, corporate Q3, and festival season collide" },
      { stat: "28 Hours", label: "shortest turnaround we have accommodated for a Portland executive departing to Tokyo" },
    ],
    destinations: [
      { name: "Japan & South Korea", context: "Delta PDX-Tokyo nonstop, Nike/Intel Asia-Pacific operations, outdoor gear manufacturing visits, anime and pop culture tourism", urgency: "Business travel booked 1–2 weeks out; Japan requires 6-month passport validity", icon: "ri-map-pin-2-line" },
      { name: "Netherlands & Western Europe", context: "Delta PDX-Amsterdam nonstop, Nike European HQ connections, cycling tourism in Netherlands and Belgium, craft beer pilgrimages to Belgium and Germany", urgency: "Schengen zone requires 3 months passport validity beyond stay; business trips booked 2–3 weeks out", icon: "ri-map-pin-2-line" },
      { name: "United Kingdom & Ireland", context: "British Airways PDX-London nonstop, tech sector connections, heritage travel (strong Irish and British ancestry in PNW)", urgency: "UK now requires 6-month passport validity for many visitors post-Brexit", icon: "ri-map-pin-2-line" },
      { name: "Canada", context: "Vancouver, BC weekend trips, Whistler ski getaways, Victoria and Gulf Islands, cross-border craft beer trails, business meetings in Vancouver tech corridor", urgency: "Passport card popular for land crossings; weekend trips planned 1–2 weeks out", icon: "ri-map-pin-2-line" },
      { name: "Mexico & Central America", context: "Surf trips to Oaxaca and Baja, eco-lodges in Costa Rica, Spanish immersion programs, yoga and wellness retreats", urgency: "Winter surf season bookings peak September through November", icon: "ri-map-pin-2-line" },
      { name: "Iceland & Nordics", context: "Icelandair PDX-Reykjavik nonstop, Nordic heritage travel, aurora tourism, outdoor recreation in Norway and Sweden", urgency: "Northern lights season (Sep–Mar) bookings often finalized 3–4 weeks before departure", icon: "ri-map-pin-2-line" },
    ],
    crossLinksTitle: "Explore Other Oregon Service Areas",
    crossLinksDesc: "We serve travelers across the Beaver State with the same fast, reliable passport processing.",
    crossLinks: [
      { name: "Eugene", route: "/cities/eugene" },
      { name: "Salem", route: "/cities/salem" },
      { name: "Bend", route: "/cities/bend" },
    ],
  },
  photoLocations: [
    { name: "Walgreens — Pearl District", address: "811 NW 23rd Ave, Portland, OR 97210", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/photo" },
    { name: "CVS — Downtown Portland", address: "908 SW 4th Ave, Portland, OR 97204", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/photo" },
    { name: "FedEx Office — SW Portland", address: "810 SW Broadway, Portland, OR 97205", hours: "Mon–Fri 7 AM – 10 PM, Sat–Sun 8 AM – 8 PM", website: "https://www.office.fedex.com/default/office" },
    { name: "The UPS Store — NW Portland", address: "1500 NW Bethany Blvd, Beaverton, OR 97006", hours: "Mon–Fri 8 AM – 7 PM", website: "https://www.theupsstore.com/print/passport-photos" },
    { name: "Walgreens — SE Hawthorne", address: "3534 SE Hawthorne Blvd, Portland, OR 97214", hours: "Daily 8 AM – 10 PM", website: "https://www.walgreens.com/photo" },
    { name: "CVS — NE Portland", address: "6320 NE Sandy Blvd, Portland, OR 97213", hours: "Daily 7 AM – 10 PM", website: "https://www.cvs.com/photo" },
    { name: "FedEx Office — Beaverton", address: "1590 NW 185th Ave, Beaverton, OR 97006", hours: "Mon–Fri 7 AM – 10 PM", website: "https://www.office.fedex.com/default/office" },
    { name: "Walgreens — Lake Oswego", address: "525 SW Mcvey Ave, Lake Oswego, OR 97034", hours: "Daily 8 AM – 9 PM", website: "https://www.walgreens.com/photo" },
  ],
  dmvLocations: [
    {
      name: "Portland Driver & Motor Vehicle Services — McLoughlin",
      address: "9301 SE McLoughlin Blvd, Portland, OR 97222",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Main Portland DMV office. Driver licenses, REAL ID, ID cards. Appointment recommended.",
      featured: true,
    },
    {
      name: "Portland Driver & Motor Vehicle Services — Swan Island",
      address: "1905 N Hayden Island Dr, Portland, OR 97217",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "North Portland DMV option. Appointment recommended.",
      featured: false,
    },
    {
      name: "Beaverton DMV Office",
      address: "14075 SW Barrows Rd, Beaverton, OR 97007",
      hours: "Mon–Fri 8:30 AM – 5:00 PM",
      note: "Washington County option for west Portland metro residents.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "PDX",
      name: "Portland International Airport",
      distance: "About 6 miles from downtown Portland",
      address: "7000 NE Airport Way, Portland, OR 97218",
      note: "Portland's primary airport with direct international flights to Amsterdam, London, Tokyo, Reykjavik, Vancouver, and more. Consistently ranked among America's best airports. The busiest travel seasons are May–August and December–January.",
      flightUrl: "https://www.flypdx.com/",
      flightLabel: "PDX Airport Website",
      featured: true,
    },
    {
      code: "SEA",
      name: "Seattle-Tacoma International Airport",
      distance: "About 175 miles north of Portland",
      address: "17801 International Blvd, SeaTac, WA 98158",
      note: "Some Portland travelers use SEA for additional flight options. Passport required for all international departures from either airport.",
      flightUrl: "https://www.portseattle.org/sea-tac",
      flightLabel: "SEA Airport Website",
      featured: false,
    },
  ],
  vitalRecordsLinks: [
    {
      icon: "ri-user-smile-line",
      label: "Birth Certificate",
      desc: "Request certified birth certificates from the Oregon Vital Records office. Online, mail, or in-person options available for Multnomah County and all Oregon counties.",
      url: "https://www.oregon.gov/oha/ph/birthdeathcertificates/",
    },
    {
      icon: "ri-file-list-2-line",
      label: "Death Certificate",
      desc: "Obtain certified death certificates through Oregon Vital Records. Required for estate and legal matters in Multnomah County.",
      url: "https://www.oregon.gov/oha/ph/birthdeathcertificates/",
    },
    {
      icon: "ri-hearts-line",
      label: "Marriage Certificate",
      desc: "Request marriage license records from the Multnomah County Clerk at 1021 SW 4th Ave. Certified copies for name changes and legal purposes.",
      url: "https://www.multco.us/recording/marriage-licenses",
    },
    {
      icon: "ri-file-copy-line",
      label: "Divorce Decree",
      desc: "Obtain certified divorce records through the Multnomah County Circuit Court. Required for name change passport corrections.",
      url: "https://www.courts.oregon.gov/courts/multnomah",
    },
  ],
};